/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useCallback } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  Icons,
  PLACEMENT,
  ToggleButton,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import Tooltip from '../../../tooltip';
import { focusStyle, getCommonValue } from '../../shared';
import { MULTIPLE_VALUE } from '../../../../constants';

const Controls = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 4px;
  margin-right: 8px;
`;

const DirectionButton = styled(ToggleButton)`
  ${focusStyle};
`;

function GrowthDirection({ selectedElements, pushUpdate }) {
  const value = getCommonValue(selectedElements, 'verticalAnchor', 'top');
  const handleChange = useCallback(
    (verticalAnchor) => {
      pushUpdate({ verticalAnchor }, true, {
        skipPresubmit: true,
      });
    },
    [pushUpdate]
  );

  return (
    <Controls
      role="group"
      aria-label={__('Text box growth direction', 'web-stories')}
    >
      <Tooltip
        title={__('Grow downward from top', 'web-stories')}
        hasTail
        placement={PLACEMENT.TOP}
      >
        <DirectionButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SQUARE}
          isToggled={
            value !== MULTIPLE_VALUE && value !== 'bottom' && value !== 'center'
          }
          onClick={() => handleChange('top')}
          aria-label={__('Grow text box downward from top edge', 'web-stories')}
        >
          <Icons.TextGrowthDown />
        </DirectionButton>
      </Tooltip>
      <Tooltip
        title={__('Grow from centre', 'web-stories')}
        hasTail
        placement={PLACEMENT.TOP}
      >
        <DirectionButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SQUARE}
          isToggled={value === 'center'}
          onClick={() => handleChange('center')}
          aria-label={__('Grow text box equally from centre', 'web-stories')}
        >
          <Icons.TextGrowthCenter />
        </DirectionButton>
      </Tooltip>
      <Tooltip
        title={__('Grow upward from bottom', 'web-stories')}
        hasTail
        placement={PLACEMENT.TOP}
      >
        <DirectionButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SQUARE}
          isToggled={value === 'bottom'}
          onClick={() => handleChange('bottom')}
          aria-label={__(
            'Grow text box upward from bottom edge',
            'web-stories'
          )}
        >
          <Icons.TextGrowthUp />
        </DirectionButton>
      </Tooltip>
    </Controls>
  );
}

GrowthDirection.propTypes = {
  selectedElements: PropTypes.arrayOf(PropTypes.object).isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default GrowthDirection;
